import type { TemplateCreateOperation } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useLazyLayerMetaModel } from '../context/useLazyLayerMetaModel'
import { isModelMetaType } from '../types/metaModel'
import type {
  TemplateFunctions,
  TemplateVersion,
  TemplateVersionInfo,
} from '../types/templateModel'
import { useOperationsContext } from '../workspace/operationsContext'
import { templateModelTypeFromCreateOperation } from './helpers'
import { useEnrichedTemplateCreateOperation } from './templateOperations/useEnrichedTemplateCreateOperation'
import { toServerTemplateFunctions } from './transformers/toServerModelConverter'
import { generateFunctions } from './transformers/toVisualModelConverter'
import { useTemplateFields } from './useTemplateFields'

export type TemplateParams = {
  versionInfo: Optional<TemplateVersionInfo>
  isFlattened: boolean
}

export const useTemplateVersion = ({
  isFlattened,
  versionInfo,
}: TemplateParams): Optional<TemplateVersion> => {
  const {
    enrichedTemplateCreationOperation,
    originalTemplate,
    isFlattened: didFlattenTemplate,
  } = useEnrichedTemplateCreateOperation(versionInfo, isFlattened)

  const { onAddOrUpdateOperation } = useOperationsContext()

  const fields = useTemplateFields({
    enrichedTemplate: enrichedTemplateCreationOperation,
  })
  const metaModel = useLazyLayerMetaModel(versionInfo?.layer.layerId)

  return useMemo(() => {
    const inInOutEntity = enrichedTemplateCreationOperation?.entity

    if (
      enrichedTemplateCreationOperation &&
      inInOutEntity &&
      metaModel &&
      versionInfo
    ) {
      const className = inInOutEntity.templateTypeName

      assertNotEmpty(className)

      const type = metaModel?.types.get(className)

      const modelTypeName = templateModelTypeFromCreateOperation(
        enrichedTemplateCreationOperation as TemplateCreateOperation,
      )

      const modelType = modelTypeName
        ? metaModel.types.get(modelTypeName)
        : undefined

      const handleUpdateTemplate = async (nextFunctions: TemplateFunctions) => {
        const transformedFunctions = toServerTemplateFunctions(nextFunctions)
        const {
          origin: _,
          versionInfo: __,
          ...creationWithoutVersionInfo
        } = enrichedTemplateCreationOperation

        const nextCreateOperation = {
          ...creationWithoutVersionInfo,
          entity: {
            ...originalTemplate!,
            ...transformedFunctions,
          },
        } satisfies TemplateCreateOperation

        await onAddOrUpdateOperation({
          operation: nextCreateOperation,
          layer: versionInfo.layer,
          block: versionInfo.block,
          // Irrelevant, you can only edit the functions of a draft, which can only exist in the workspace.
          shouldOpenUnifiedBranchWorkspace: false,
        })
      }

      assertNotEmpty(inInOutEntity.origin)

      const path = `{${inInOutEntity.name}}`

      const functions = generateFunctions({
        parent: inInOutEntity,
        updateField: handleUpdateTemplate,
        path,
        meta: type,
        isParentNotRelevant: false,
      })

      const templateVersion: TemplateVersion = {
        isFlattened: !!didFlattenTemplate,
        origin: `{${versionInfo.identifier}}`,
        versionInfo,
        layer: versionInfo.layer,
        block: versionInfo.block,
        ...(enrichedTemplateCreationOperation.entity?.documentation
          ? {
              documentation:
                enrichedTemplateCreationOperation.entity?.documentation,
            }
          : {}),
        type: isModelMetaType(type) ? type : undefined,
        typeName: className,
        path,
        modelType: isModelMetaType(modelType) ? modelType : undefined,
        modelTypeName,
        declaredFunctions: functions.declaredFunctions,
        dynamicFunctions: functions.dynamicFunctions,
        replaces: inInOutEntity?.replaces,
        fields,
        updateTemplate: async (nextVersion) => {
          assertNotEmpty(originalTemplate)

          const {
            origin: _,
            versionInfo: __,
            ...creationWithoutVersionInfo
          } = enrichedTemplateCreationOperation
          const nextTemplateCreateOperation = {
            ...creationWithoutVersionInfo,
            entity: {
              ...originalTemplate,
              replaces: nextVersion.replaces,
              documentation: nextVersion.documentation?.value,
              name: nextVersion.versionInfo.identifier,
              parent: nextVersion.versionInfo.parentId,
              ...toServerTemplateFunctions({
                declaredFunctions: nextVersion.declaredFunctions,
                dynamicFunctions: nextVersion.dynamicFunctions,
              }),
            },
          } satisfies TemplateCreateOperation

          await onAddOrUpdateOperation({
            operation: nextTemplateCreateOperation,
            layer: versionInfo.layer,
            block: versionInfo.block,
            // Irrelevant, you can only edit the functions of a draft, which can only exist in the workspace.
            shouldOpenUnifiedBranchWorkspace: false,
          })
        },
      }

      return templateVersion
    } else if (versionInfo) {
      const templateVersion: TemplateVersion = {
        versionInfo,
        isFlattened: !!didFlattenTemplate,
        origin: `{${versionInfo.identifier}}`,
        layer: versionInfo.layer,
        block: versionInfo.block,
        type: undefined,
        typeName: undefined,
        path: `{${versionInfo.identifier}}`,
        modelTypeName: undefined,
        fields: [],
        updateTemplate: () => {
          throw new Error(
            'Template versions without an in/out entity cannot be updated',
          )
        },
      }

      return templateVersion
    } else {
      return undefined
    }
  }, [
    enrichedTemplateCreationOperation,
    versionInfo,
    didFlattenTemplate,
    fields,
    originalTemplate,
    onAddOrUpdateOperation,
    metaModel,
  ])
}
